import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createVariableExpense, updateVariableExpense, deleteVariableExpense,
} from '../../../api/treasury';
import { MOIS_COURT } from '../engine/constants';
import { useP } from '../../../i18n/useP';

const parseNum = (str) => {
  if (typeof str === 'number') return str;
  if (!str) return 0;
  const n = parseFloat(String(str).replace(/[\s']/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

function MonthInput({ value, onCommit, disabled = false }) {
  const [draft, setDraft] = useState(null);
  const display = draft !== null
    ? draft
    : (value ? Math.round(value).toLocaleString('de-CH').replace(/\./g, "'") : '');
  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      disabled={disabled}
      onFocus={() => setDraft(String(value || ''))}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null) {
          const parsed = parseNum(draft);
          if (parsed !== value) onCommit(parsed);
        }
        setDraft(null);
      }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

function TextInput({ value, onCommit }) {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="text"
      value={draft !== null ? draft : (value || '')}
      onFocus={() => setDraft(value || '')}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null && draft.trim() !== (value || '')) onCommit(draft.trim());
        setDraft(null);
      }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

export default function PurchasesTab({ tenantId, scenarioId, locked, revenues, variables, onChange, startMonth = 0, horizon = 12 }) {
  const { t } = useTranslation();
  const p = useP();
  // Colonnes = mois planifiés depuis le mois de départ, sur la durée de projection.
  const H = Math.min(12, Math.max(1, Number(horizon) || 12));
  const winMonths = Array.from({ length: H }, (_, i) => (Number(startMonth) + i) % 12);
  const [error, setError] = useState(null);

  async function patch(eid, payload) {
    const r = await updateVariableExpense(tenantId, scenarioId, eid, payload);
    if (r.error) setError(r.error);
    else { setError(null); onChange(); }
  }

  async function addRow(driver_type) {
    const payload = {
      scenario_id: scenarioId,
      label: driver_type === 'pct_revenue' ? p('Nouvelle charge en %CA') : p('Nouvelle charge manuelle'),
      driver_type,
      payment_lag: 0,
      vat_recoverable: true,
      display_order: variables.length,
      ...(driver_type === 'pct_revenue'
        ? { revenue_ref_id: revenues[0]?.id || null, pct_rate: 0 }
        : { monthly_values: Array(12).fill(0) }),
    };
    const r = await createVariableExpense(tenantId, scenarioId, payload);
    if (r.error) setError(r.error);
    else { setError(null); onChange(); }
  }

  async function removeRow(eid) {
    if (!window.confirm(p('Supprimer cette ligne ?'))) return;
    const r = await deleteVariableExpense(tenantId, scenarioId, eid);
    if (r.error) setError(r.error);
    else { setError(null); onChange(); }
  }

  async function patchMonth(expense, mIdx, value) {
    const newValues = [...(expense.monthly_values || Array(12).fill(0))];
    newValues[mIdx] = value;
    await patch(expense.id, { monthly_values: newValues });
  }

  const sectionB = variables.filter(row => row.driver_type === 'manual');

  return (
    <div>
      <h2 className="tres-section-title">{p('Achats')}</h2>
      <p className="tres-section-subtitle">
        {p('Saisie manuelle sur les')} {H}{' '}
        {p("mois planifiés. Les coûts variables en % d'une ligne de revenu s'éditent désormais directement sur la ligne (onglet Revenus).")}
      </p>

      {error && <div className="treasury__error">{error} <button onClick={() => setError(null)}>×</button></div>}

      {/* ── Saisie manuelle (mois planifiés) ─────────────────────────── */}
      <h3 style={{ fontSize: 13, color: 'var(--color-navy)', margin: '20px 0 8px' }}>
        {p('Saisie manuelle —')} {H} {p('mois')}
      </h3>
      <table className="tres-table tres-edit-table">
        <thead>
          <tr>
            <th>{t('phrases:p_f5485bbae8')}</th>
            {winMonths.map((cal, i) => <th key={i}>{MOIS_COURT[cal]}</th>)}
            <th>{p('Délai')}</th>
            <th>{p('TVA')}</th>
            <th className="col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {sectionB.map(row => (
            <tr key={row.id}>
              <td><TextInput value={row.label} onCommit={val => patch(row.id, { label: val })} /></td>
              {Array.from({ length: H }).map((_, m) => (
                <td key={m}>
                  <MonthInput
                    value={Number(row.monthly_values?.[m] || 0)}
                    onCommit={val => patchMonth(row, m, val)}
                  />
                </td>
              ))}
              <td>
                <select
                  value={row.payment_lag}
                  onChange={e => patch(row.id, { payment_lag: parseInt(e.target.value, 10) })}
                  disabled={locked}
                >
                  {[0,1,2,3].map(d => <option key={d} value={d}>M+{d}</option>)}
                </select>
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={!!row.vat_recoverable}
                  onChange={e => patch(row.id, { vat_recoverable: e.target.checked })}
                  disabled={locked}
                />
              </td>
              <td className="col-actions">
                <button onClick={() => removeRow(row.id)} disabled={locked} title={t('common:actions.delete')}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!locked && (
        <button className="tres-add-row" onClick={() => addRow('manual')}>
          {p('+ Ajouter une charge manuelle')}
        </button>
      )}
    </div>
  );
}
